import { Code, Stack, Text, Title } from "@mantine/core";
import { CodeBlock } from "./JsonView";
import type { CodeLanguage } from "./highlight";

type Block = { kind: "h"; level: number; text: string } | { kind: "p"; text: string } | { kind: "code"; lang: string; code: string };

const FENCE_LANG: Record<string, CodeLanguage> = { ts: "ts", typescript: "ts", js: "ts", sh: "shell", bash: "shell", shell: "shell", json: "json", python: "python", html: "html" };

function parse(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fence = line.match(/^```(\w*)/);
    if (fence) {
      const body: string[] = [];
      for (i++; i < lines.length && !lines[i].startsWith("```"); i++) body.push(lines[i]);
      blocks.push({ kind: "code", lang: fence[1], code: body.join("\n") });
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) {
      blocks.push({ kind: "h", level: h[1].length, text: h[2] });
      continue;
    }
    if (!line.trim()) continue;
    const para = [line];
    while (i + 1 < lines.length && lines[i + 1].trim() && !/^(```|#)/.test(lines[i + 1])) para.push(lines[++i]);
    blocks.push({ kind: "p", text: para.join(" ") });
  }
  return blocks;
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`)/g).map((part, i) => (part.startsWith("`") && part.endsWith("`") ? <Code key={i}>{part.slice(1, -1)}</Code> : part))}
    </>
  );
}

/** Renders the small Markdown subset the package READMEs use: headings, paragraphs, inline code and fenced code. */
export function Readme({ markdown, skipTitle = false }: { markdown: string; skipTitle?: boolean }) {
  const blocks = parse(markdown).filter((b, i) => !(skipTitle && i === 0 && b.kind === "h" && b.level === 1));
  return (
    <Stack gap="sm">
      {blocks.map((b, i) =>
        b.kind === "h" ? (
          <Title key={i} order={Math.min(6, b.level + 2) as 3 | 4 | 5 | 6} fz={b.level <= 2 ? 16 : 14} mt={i ? "xs" : 0}>
            {b.text}
          </Title>
        ) : b.kind === "p" ? (
          <Text key={i} fz="sm">
            <Inline text={b.text} />
          </Text>
        ) : (
          <CodeBlock key={i} code={b.code} language={FENCE_LANG[b.lang]} />
        ),
      )}
    </Stack>
  );
}
